import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import VideoCard from '@/components/feature/VideoCard';
import { getVideoById, getRelatedVideos, categories } from '@/mocks/videos';

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const videoId = parseInt(id || '0', 10);
  const video = getVideoById(videoId);
  const relatedVideos = video ? getRelatedVideos(video, 6) : [];
  const category = video ? categories.find(c => c.id === video.category) : null;

  if (!video) {
    return (
      <>
        <ScrollToTop />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="pt-20 pb-12 flex items-center justify-center">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500 mb-4">未找到该视频</p>
              <Link
                to="/"
                className="inline-flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 font-medium cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                <span>返回首页</span>
              </Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: video.title,
    description: video.description,
    image: video.posterUrl,
    author: {
      '@type': 'Organization',
      name: '555电影',
    },
    datePublished: `${video.year}-01-01`,
    publisher: {
      '@type': 'Organization',
      name: '555电影',
    },
  };

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: `${video.title} - 555电影`,
    description: video.description,
    url: `https://www.555movie.com/detail/${video.id}`,
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(webPageSchema)}</script>
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-12">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-[1200px] mx-auto">
              <div className="mb-6">
                <Breadcrumb
                  items={[
                    { label: '首页', href: '/' },
                    { label: category?.name || '分类', href: `/category/${video.category}` },
                    { label: video.title },
                  ]}
                />
              </div>

              <div className="flex flex-col lg:flex-row gap-6 md:gap-8">
                <div className="lg:w-[300px] flex-shrink-0">
                  <div className="relative overflow-hidden rounded-lg aspect-[3/4] bg-background-200">
                    <img
                      src={video.posterUrl}
                      alt={video.title}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>

                <div className="flex-1">
                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                    {video.title}
                  </h1>

                  <div className="flex flex-wrap items-center gap-3 mb-4">
                    <span className="px-2 py-0.5 rounded text-xs font-medium bg-primary-500 text-white">
                      ★ {video.rating}
                    </span>
                    <span className="text-sm text-foreground-500">{video.year}</span>
                    <span className="text-sm text-foreground-500">{video.region}</span>
                    <span className="px-2 py-0.5 rounded text-xs font-medium bg-secondary-100 text-secondary-800">
                      {category?.name}
                    </span>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      video.status === '连载中' ? 'bg-accent-100 text-accent-800' : 'bg-background-200 text-foreground-600'
                    }`}>
                      {video.status}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-6 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="text-foreground-500 whitespace-nowrap">导演：</span>
                      <span className="text-foreground-800">{video.director}</span>
                    </div>
                    {video.actors.length > 0 && (
                      <div className="flex items-center gap-2">
                        <span className="text-foreground-500 whitespace-nowrap">主演：</span>
                        <span className="text-foreground-800">{video.actors.join(' / ')}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <span className="text-foreground-500 whitespace-nowrap">集数：</span>
                      <span className="text-foreground-800">共{video.episodes}集</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-foreground-500 whitespace-nowrap">地区：</span>
                      <span className="text-foreground-800">{video.region}</span>
                    </div>
                  </div>

                  <div className="mb-6">
                    <h3 className="text-base font-heading font-semibold text-foreground-950 mb-2">剧情简介</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{video.description}</p>
                  </div>

                  <div className="bg-background-900 rounded-xl p-6 flex flex-col items-center justify-center min-h-[200px]">
                    <div className="w-16 h-16 flex items-center justify-center rounded-full bg-primary-500/20 mb-4">
                      <i className="ri-play-fill text-3xl text-primary-500 ml-1"></i>
                    </div>
                    <p className="text-foreground-300 text-sm text-center">
                      请选择下方剧集开始观看
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-10">
                <h2 className="text-lg font-heading font-semibold text-foreground-950 mb-4">
                  剧集列表（共{video.episodes}集）
                </h2>
                <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-12 gap-2">
                  {Array.from({ length: Math.min(video.episodes, 36) }, (_, i) => (
                    <button
                      key={i + 1}
                      className="px-3 py-2 rounded-lg text-sm font-medium bg-background-100 text-foreground-700 hover:bg-primary-500 hover:text-white transition-colors cursor-pointer whitespace-nowrap"
                    >
                      第{i + 1}集
                    </button>
                  ))}
                </div>
                {video.episodes > 36 && (
                  <p className="text-xs text-foreground-400 mt-3">
                    仅显示前36集，更多剧集请使用搜索功能
                  </p>
                )}
              </div>

              {relatedVideos.length > 0 && (
                <div className="mt-12">
                  <h2 className="text-xl font-heading font-bold text-foreground-950 mb-6">
                    相关推荐
                  </h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-5">
                    {relatedVideos.map((v) => (
                      <VideoCard
                        key={v.id}
                        id={v.id}
                        title={v.title}
                        category={v.category}
                        year={v.year}
                        rating={v.rating}
                        posterUrl={v.posterUrl}
                        status={v.status}
                        categoryName={categories.find(c => c.id === v.category)?.name || ''}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}